import { useEffect, useState } from 'react';
import { Modal } from 'antd';
import moment from 'moment';
import APIClient from '../api/api';
import { ClickerEvidence } from '../api/models';
import { Callout, StatusTag, TagTone } from './ui';

export interface Props {
  evidenceId: number;
  onClose: () => void;
}

type View = 'screenshot' | 'rendered' | 'source';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; evidence: ClickerEvidence; screenshotUrl: string | null; dom: string | null };

// The snapshot is shown in a fully sandboxed frame with a policy that loads
// nothing: no scripts, no registrar assets, no navigation. Only inline styles
// and data: images survive, which is enough to read the markup.
const SNAPSHOT_CSP =
  '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data:; font-src data:">';

export function wrapDomForViewing(html: string): string {
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${SNAPSHOT_CSP}`);
  }
  return `<!DOCTYPE html><html><head>${SNAPSHOT_CSP}</head><body>${html}</body></html>`;
}

const kindLabel: Record<string, string> = {
  raa_verification: 'RAA verification',
  fast_transfer_click: 'Fast-Transfer authorization',
};

function outcomeTone(outcome: string): TagTone {
  switch (outcome) {
    case 'verified':
    case 'agreed':
    case 'already_active':
      return 'success';
    case 'verified_domain_mismatch':
    case 'rejected':
    case 'terms_mismatch':
      return 'warn';
    case 'captcha':
    case 'anomaly':
    case 'unexpected_domain':
      return 'danger';
    default:
      return 'neutral';
  }
}

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

export default function ClickerEvidenceModal({ evidenceId, onClose }: Props) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [view, setView] = useState<View>('screenshot');

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const evidence = await APIClient.getClickerEvidence(evidenceId);
        let screenshotUrl: string | null = null;
        let dom: string | null = null;
        if (evidence.hasScreenshot) {
          const blob = await APIClient.getClickerEvidenceScreenshot(evidenceId);
          objectUrl = URL.createObjectURL(blob);
          screenshotUrl = objectUrl;
        }
        if (evidence.hasDom) {
          dom = await APIClient.getClickerEvidenceDom(evidenceId);
        }
        if (cancelled) return;
        setView(evidence.hasScreenshot ? 'screenshot' : 'rendered');
        setState({ kind: 'loaded', evidence, screenshotUrl, dom });
      } catch (e: any) {
        if (!cancelled) setState({ kind: 'error', message: e?.message || 'Failed to load evidence.' });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [evidenceId]);

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={1040}
      closable={false}
      destroyOnClose
      styles={{
        content: { padding: 0, background: 'rgb(var(--color-panel) / 1)', borderRadius: 10, overflow: 'hidden' },
        body: { padding: 0 },
      }}
    >
      <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-border">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">Clicker evidence #{evidenceId}</div>
          {state.kind === 'loaded' && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[15px] font-semibold text-text">{state.evidence.domain}</span>
              <StatusTag tone={outcomeTone(state.evidence.outcome)}>{state.evidence.outcome}</StatusTag>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-text-3 hover:text-text text-[18px] leading-none bg-transparent border-0 cursor-pointer"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="max-h-[75vh] overflow-auto">
        {state.kind === 'loading' && (
          <div className="px-6 py-12 text-center text-text-3 text-[12.5px]">Loading…</div>
        )}
        {state.kind === 'error' && (
          <div className="px-6 py-6">
            <Callout tone="danger" title="Could not load evidence">
              {state.message}
            </Callout>
          </div>
        )}
        {state.kind === 'loaded' && (
          <EvidenceBody
            evidence={state.evidence}
            screenshotUrl={state.screenshotUrl}
            dom={state.dom}
            view={view}
            onView={setView}
          />
        )}
      </div>
    </Modal>
  );
}

function EvidenceBody({
  evidence,
  screenshotUrl,
  dom,
  view,
  onView,
}: {
  evidence: ClickerEvidence;
  screenshotUrl: string | null;
  dom: string | null;
  view: View;
  onView: (v: View) => void;
}) {
  const stored = evidence.hasScreenshot || evidence.hasDom;
  return (
    <div className="px-6 py-4 flex flex-col gap-4">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-[12px]">
        <MetaItem label="Kind" value={kindLabel[evidence.kind] || evidence.kind} />
        <MetaItem label="Job" value={`#${evidence.jobId}`} mono />
        <MetaItem label="Account" value={evidence.ncUsername || '—'} mono />
        <MetaItem label="Captured" value={moment(evidence.createdAt).format('YYYY-MM-DD HH:mm:ss')} mono />
        {evidence.termsHash && <MetaItem label="Terms hash" value={evidence.termsHash} mono />}
        <MetaItem
          label="Assets"
          value={
            stored
              ? [
                  evidence.hasScreenshot ? `screenshot ${formatBytes(evidence.screenshotSize)}` : null,
                  evidence.hasDom ? `DOM ${formatBytes(evidence.domSize)}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'not stored'
          }
        />
      </dl>

      {(evidence.storeError || !stored) && (
        <Callout tone="warn" title={evidence.storeError ? 'Evidence was not stored durably' : 'The sidecar returned no capture'}>
          {evidence.storeError ? (
            <div className="font-mono text-[11.5px] break-all">{evidence.storeError}</div>
          ) : (
            <div className="text-[11.5px]">
              The run reported no screenshot or DOM, so there was nothing to store. A sidecar built before
              evidence was returned inline behaves this way: rebuild and restart it.
            </div>
          )}
          {(evidence.sidecarScreenshotPath || evidence.sidecarDomPath) && (
            <div className="mt-1 text-[11.5px]">
              The sidecar kept a local copy:{' '}
              <span className="font-mono break-all">
                {[evidence.sidecarScreenshotPath, evidence.sidecarDomPath].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </Callout>
      )}

      {stored && (
        <>
          <div className="flex gap-1">
            {evidence.hasScreenshot && (
              <ViewButton active={view === 'screenshot'} onClick={() => onView('screenshot')}>
                Screenshot
              </ViewButton>
            )}
            {evidence.hasDom && (
              <>
                <ViewButton active={view === 'rendered'} onClick={() => onView('rendered')}>
                  Rendered DOM
                </ViewButton>
                <ViewButton active={view === 'source'} onClick={() => onView('source')}>
                  DOM source
                </ViewButton>
              </>
            )}
          </div>

          {view === 'screenshot' && screenshotUrl && (
            <div className="border border-border rounded-md overflow-auto bg-bg">
              <img src={screenshotUrl} alt={`Screenshot of the ${kindLabel[evidence.kind] || evidence.kind} page for ${evidence.domain}`} className="block max-w-full" />
            </div>
          )}
          {view === 'rendered' && dom !== null && (
            <div className="border border-border rounded-md overflow-hidden" style={{ height: '60vh' }}>
              <iframe
                sandbox=""
                srcDoc={wrapDomForViewing(dom)}
                title="DOM snapshot"
                style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              />
            </div>
          )}
          {view === 'source' && dom !== null && (
            <pre className="text-[11px] font-mono text-text-2 bg-bg border border-border rounded-md p-3 m-0 max-h-[60vh] overflow-auto whitespace-pre-wrap break-all">
              {dom}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

function MetaItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">{label}</dt>
      <dd className={`m-0 text-text-2 break-all ${mono ? 'font-mono text-[11.5px]' : ''}`}>{value}</dd>
    </div>
  );
}

function ViewButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-2 py-0.5 rounded text-[11px] font-medium border cursor-pointer ${
        active
          ? 'bg-accent-soft text-accent border-accent/30'
          : 'bg-transparent text-text-3 border-border hover:text-text-2'
      }`}
    >
      {children}
    </button>
  );
}
